import type { AlertDialog } from '@base-ui/react';

import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { QuotationQueryMetadata } from '~/api/quotations';
import type { Quotation, QuotationStatus } from '~/api/quotations/types';
import type { Brand } from '~/types/user';

import { useClientsWithTrashed } from '~/api/clients';
import { quotationsQueryOptions, useQuotationFilters } from '~/api/quotations';
import { useUser } from '~/api/user';
import { createAppColumnHelper, Table } from '~/components';
import { ColumnIds } from '~/constants/table';
import { useIntl } from '~/hooks/useIntl';
import { noop } from '~/utils/functions';

import { QuotationClientCell } from './QuotationsTableCells/QuotationClientCell';
import { QuotationDocumentDateCell } from './QuotationsTableCells/QuotationDocumentDateCell';
import { QuotationMobileCell } from './QuotationsTableCells/QuotationMobileCell';
import { QuotationOptionsCell } from './QuotationsTableCells/QuotationOptionsCell';
import { QuotationStatusCell } from './QuotationsTableCells/QuotationStatusCell';
import { QuotationTitleCell } from './QuotationsTableCells/QuotationTitleCell';
import { QuotationTotalCell } from './QuotationsTableCells/QuotationTotalCell';

export type DialogHandles = Record<
  'approveQuotationHandle' | 'rejectQuotationHandle' | 'deleteQuotationHandle',
  AlertDialog.Handle<Quotation>
>;

const columnHelper = createAppColumnHelper<Quotation>();

export const useQuotationsTableColumns = ({ dialogHandles }: { dialogHandles: DialogHandles }) => {
  const { data: clients } = useClientsWithTrashed();
  const filters = useQuotationsTableFilters();

  const { t } = useTranslation(['common', 'quotations']);

  return useMemo(
    () =>
      columnHelper.columns([
        // Selector
        columnHelper.display({
          id: ColumnIds.selector,
          header: () => <Table.SelectAll />,
          cell: ({ row }) => <Table.SelectRow row={row} />,
          meta: {
            styles: { size: 'max-content' },
          },
        }),

        // Title & entry numbers
        columnHelper.accessor(({ title }) => title, {
          id: ColumnIds.quotationsTitle,
          header: t('quotations:overview.columns.entryNumber'),
          cell: ({ row: { original: quotation } }) => <QuotationTitleCell quotation={quotation} />,
          meta: {
            styles: { size: '3fr', minSize: 125 },
            isResizable: true,
          },
        }),

        // Brand (hidden, column filter only)
        columnHelper.accessor(({ brandId }) => brandId, {
          id: ColumnIds.quotationsBrand,
          meta: {
            filterOptions: filters.brands,
          },
        }),

        // Client
        columnHelper.accessor(({ clientId }) => clients.find((client) => client.id === clientId)?.name ?? '', {
          id: ColumnIds.quotationsClient,
          header: t('quotations:overview.columns.client'),
          cell: ({ row: { original: quotation } }) => <QuotationClientCell quotation={quotation} />,
          meta: {
            styles: { size: '2fr', minSize: 125 },
            isResizable: true,
            filterOptions: filters.clients,
          },
        }),

        // Document date
        columnHelper.accessor(({ documentDate }) => documentDate, {
          id: ColumnIds.quotationsDocDate,
          header: t('quotations:overview.columns.date'),
          cell: ({ row: { original: quotation } }) => <QuotationDocumentDateCell quotation={quotation} />,
          meta: {
            styles: { size: 'max-content', minSize: 100 },
            isResizable: true,
          },
        }),

        // Doc year (hidden, column filter only)
        columnHelper.accessor(({ documentDate }) => dayjs(documentDate).year(), {
          id: ColumnIds.quotationsDocYear,
          meta: {
            filterOptions: filters.years,
          },
        }),

        // Doc quarter (hidden, column filter only)
        columnHelper.accessor(({ documentDate }) => `${dayjs(documentDate).year()} ${dayjs(documentDate).quarter()}`, {
          id: ColumnIds.quotationsDocQuarter,
          meta: {
            filterOptions: filters.quarters,
          },
        }),

        // Status
        columnHelper.accessor(({ status }) => status, {
          id: ColumnIds.quotationsStatus,
          header: t('quotations:overview.columns.status.title'),
          cell: ({ row: { original: quotation } }) => <QuotationStatusCell quotation={quotation} />,
          meta: {
            styles: { size: 'max-content', minSize: 120 },
            filterOptions: filters.statuses,
          },
        }),

        // Total
        columnHelper.accessor(({ calculationData }) => calculationData.coExclVat, {
          id: ColumnIds.quotationsTotal,
          header: t('quotations:overview.columns.total'),
          cell: ({ getValue }) => <QuotationTotalCell value={getValue()} />,
          meta: {
            styles: { size: 'max-content', minSize: 100, justify: 'end' },
          },
        }),

        // Options
        columnHelper.display({
          id: ColumnIds.options,
          cell: ({ row: { original: quotation } }) => <QuotationOptionsCell dialogHandles={dialogHandles} quotation={quotation} />,
          meta: {
            styles: { size: 'max-content', minSize: 40 },
          },
        }),

        // mobile
        columnHelper.display({
          id: ColumnIds.quotationsMobile,
          cell: ({ row: { original: quotation } }) => <QuotationMobileCell dialogHandles={dialogHandles} quotation={quotation} />,
          meta: { mobile: true },
        }),
      ]),
    [clients, dialogHandles, filters, t],
  );
};

export const usePrefetchNextPage = (nextPage: number | null, queryMetadata: QuotationQueryMetadata) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (nextPage === null) return;

    queryClient
      .query(
        quotationsQueryOptions({
          ...queryMetadata,
          pagination: { ...queryMetadata.pagination, pageIndex: queryMetadata.pagination.pageIndex + 1 },
        }),
      )
      .catch(noop);
  }, [nextPage, queryClient, queryMetadata]);
};

const useQuotationsTableFilters = () => {
  const { data: user } = useUser();
  const { data: clients } = useClientsWithTrashed();
  const { data: quotationFilters } = useQuotationFilters();

  const { compareForSort } = useIntl();
  const { t } = useTranslation(['filters', 'quotations']);

  const clientFilterOptions = useMemo(
    () =>
      quotationFilters.clientIds
        .map((clientId) => ({
          value: clientId,
          label: clients.find((client) => client.id === clientId)?.name ?? '',
        }))
        .sort((a, b) => compareForSort(a.label, b.label)),
    [clients, compareForSort, quotationFilters.clientIds],
  );

  const brandFilterOptions = useMemo(
    () =>
      quotationFilters.brandIds
        .map((brandId) => user.brands.find((brand) => brand.id === brandId) ?? null)
        .filter((brand): brand is Brand => brand !== null)
        .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || compareForSort(a.companyName, b.companyName))
        .map((brand) => ({ value: brand.id, label: brand.companyName })),
    [compareForSort, quotationFilters.brandIds, user.brands],
  );

  const yearFilterOptions = useMemo(
    () =>
      quotationFilters.years
        .map((year) => ({
          value: year,
          label: `${year}`,
        }))
        .sort((a, b) => -1 * compareForSort(a.label, b.label)),
    [quotationFilters.years, compareForSort],
  );

  const quarterFilterOptions = useMemo(
    () =>
      quotationFilters.quarters
        .map((yearQuarter) => ({
          value: yearQuarter,
          label: t('filters:quarter.option', { year: yearQuarter.split('-')[0], quarter: yearQuarter.split('-')[1] }),
        }))
        .sort((a, b) => {
          const [yearA, quarterA] = a.value.split('-').map((value) => +value);
          const [yearB, quarterB] = b.value.split('-').map((value) => +value);

          return yearB === yearA ? quarterB - quarterA : yearB - yearA;
        }),
    [quotationFilters.quarters, t],
  );

  const statusFilterOptions = useMemo(
    () =>
      quotationFilters.statuses
        .map((status) => ({
          value: status,
          label: t(`quotations:overview.columns.status.options.${status}`),
        }))
        .sort((a, b) => {
          const order: QuotationStatus[] = ['Sent', 'Expired', 'Approved', 'Rejected'];
          return order.indexOf(a.value) - order.indexOf(b.value);
        }),
    [quotationFilters.statuses, t],
  );

  return useMemo(
    () => ({
      clients: clientFilterOptions,
      brands: brandFilterOptions,
      years: yearFilterOptions,
      quarters: quarterFilterOptions,
      statuses: statusFilterOptions,
    }),
    [brandFilterOptions, clientFilterOptions, quarterFilterOptions, statusFilterOptions, yearFilterOptions],
  );
};
